// Branch Forecast — network-flow model. Every hub feeds every branch: a branch's
// volume = its home hub (Local) + H2H bits from all other hubs. Each origin hub's
// share of a branch comes from seeded per-origin flow actuals (branch_flow_actuals).
// A branch's contribution from a hub = that hub's forecast × the branch's share of
// it; the branch total sums all hubs. Setting a lane (Routing Map) re-labels an
// origin's routing H2H → Direct and changes its transit.
//
// Expand a branch to see every origin hub as a row (Local / H2H / Direct) with its
// share, transit and per-period volume, plus a bold branch-total row. A top tab
// switches the columns between Daily and Weekly.

import { Fragment, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Card, Field, Input, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, fmtDow, fmtN, startOfWeekSat, today } from "../../lib/format";
import { haversine, transitDays } from "../../lib/geo";
import type { Branch, Customer, DirectLane, Hub, HubEvent } from "../../lib/types";
import { forecastHubDay } from "../../lib/forecast/engine";
import { flowShares, shareOf } from "../../lib/forecast/flowShares";
import { listCustomers } from "../customers/customersApi";
import { listHubs } from "../hubs/hubsApi";
import { listBranches } from "../branches/branchesApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "./overridesApi";
import { listAccountDirectLanes } from "../network/directLanesApi";
import { getFlowShareTotals, listAccountBranchFlow, type BranchFlow } from "../actuals/branchFlowApi";
import { listActiveLocks } from "./forecastLockApi";
import { useTransitAware } from "../../lib/forecast/useTransitAware";

const ALL = "__all__";
const WEEK_OPTS = [4, 8, 13, 26, 52];
const weekDatesFrom = (start: string) => Array.from({ length: 7 }, (_, d) => addDays(start, d));

interface OriginRow {
  hubId: string;
  hubName: string;
  routing: "Local" | "H2H" | "Direct";
  sharePct: number;
  transit: number | null;
  byPeriod: number[];
  total: number;
}

export default function BranchForecastPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [directLanes, setDirectLanes] = useState<DirectLane[]>([]);
  const [shareFlow, setShareFlow] = useState<BranchFlow[]>([]); // trailing → shares
  const [rangeFlow, setRangeFlow] = useState<BranchFlow[]>([]); // range → actual view
  const [error, setError] = useState<string | null>(null);

  const [hubId, setHubId] = useState(ALL);
  const [mode, setMode] = useState<"forecast" | "actual">("forecast");
  const [anchor, setAnchor] = useState(today());
  const [numWeeks, setNumWeeks] = useState(4);
  const [transitAware, setTransitAware] = useTransitAware();
  const [expanded, setExpanded] = useState<Set<string>>(new Set()); // expanded branches
  const [openWeeks, setOpenWeeks] = useState<Set<string>>(new Set()); // weeks expanded to days
  const [lockedWeeks, setLockedWeeks] = useState<Set<string>>(new Set()); // weeks with an active lock

  useEffect(() => {
    if (!accountId) return;
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listBranches(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountDirectLanes(accountId),
    ])
      .then(([c, h, b, ov, ev, dl]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setBranches(b.filter((x) => x.active));
        setOverrides(ov);
        setEvents(ev);
        setDirectLanes(dl);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load branch forecast data.")));
    listActiveLocks(accountId)
      .then((lk) => setLockedWeeks(new Set(lk.map((l) => l.weekStart))))
      .catch(() => {});
  }, [accountId]);

  const weeks = useMemo(() => {
    const w0 = startOfWeekSat(anchor);
    return Array.from({ length: numWeeks }, (_, k) => {
      const ws = addDays(w0, k * 7);
      return { weekStart: ws, weekEnd: addDays(ws, 6), dates: weekDatesFrom(ws) };
    });
  }, [anchor, numWeeks]);
  const rangeStart = weeks[0]?.weekStart ?? anchor;
  const rangeEnd = weeks[weeks.length - 1]?.weekEnd ?? anchor;

  // trailing 4 weeks of flow → origin-hub shares (aggregated server-side: ~500 rows)
  useEffect(() => {
    if (!accountId) return;
    getFlowShareTotals(accountId, addDays(rangeStart, -28), addDays(rangeStart, -1))
      .then(setShareFlow)
      .catch((e) => setError(getErrorMessage(e, "Failed to load flow shares.")));
  }, [accountId, rangeStart]);

  // selected range flow (only for the Actuals view)
  useEffect(() => {
    if (!accountId || mode !== "actual") return;
    listAccountBranchFlow(accountId, rangeStart, rangeEnd).then(setRangeFlow).catch(() => {});
  }, [accountId, mode, rangeStart, rangeEnd]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const shares = useMemo(() => flowShares(shareFlow), [shareFlow]);
  const hubById = useMemo(() => new Map(hubs.map((h) => [h.id, h])), [hubs]);

  // Columns are WEEKS; a week expands to its 7 day-columns on click (like Yearly).
  // So the full year loads as ~52 week columns and day detail is on demand — no
  // 364-column freeze. `cols` is the flattened value-column sequence.
  const cols = useMemo(() => {
    const out: Array<{ key: string; label: string; dates: string[]; weekStart: string; isDay: boolean }> = [];
    for (const w of weeks) {
      if (openWeeks.has(w.weekStart)) {
        for (const d of w.dates) out.push({ key: d, label: fmtDow(d), dates: [d], weekStart: w.weekStart, isDay: true });
      } else {
        out.push({ key: w.weekStart, label: fmtDate(w.weekEnd), dates: w.dates, weekStart: w.weekStart, isDay: false });
      }
    }
    return out;
  }, [weeks, openWeeks]);
  const toggleWeek = (ws: string) =>
    setOpenWeeks((s) => {
      const n = new Set(s);
      if (n.has(ws)) n.delete(ws);
      else n.add(ws);
      return n;
    });

  const hubVolOnDay = useMemo(() => {
    const cache = new Map<string, number>();
    return (hid: string, d: string) => {
      const k = `${hid}|${d}`;
      const hit = cache.get(k);
      if (hit !== undefined) return hit;
      const v = forecastHubDay(customers, hid, d, overrideMap, events);
      cache.set(k, v);
      return v;
    };
  }, [customers, overrideMap, events]);

  // actual flow indexed by origin|branch|date (for the Actuals view)
  const actualIdx = useMemo(() => {
    const m = new Map<string, number>();
    if (mode !== "actual") return m;
    for (const f of rangeFlow) m.set(`${f.originHubId}|${f.branchId}|${f.date}`, f.volume);
    return m;
  }, [mode, rangeFlow]);

  const transitFor = (fromId: string, toLat?: number | null, toLng?: number | null): number | null => {
    const f = hubById.get(fromId);
    if (!f || f.lat == null || f.lng == null || toLat == null || toLng == null) return null;
    return transitDays(haversine(f.lat, f.lng, toLat, toLng));
  };

  // active lanes by origin|branch (Direct/H2H route override) within range
  const laneByPair = useMemo(() => {
    const m = new Map<string, DirectLane>();
    for (const l of directLanes) {
      if (l.customerId || !l.active) continue;
      const overlaps = (!l.effectiveDate || l.effectiveDate <= rangeEnd) && (!l.endDate || l.endDate >= rangeStart);
      if (overlaps) m.set(`${l.originHubId}|${l.branchId}`, l);
    }
    return m;
  }, [directLanes, rangeStart, rangeEnd]);

  const rows = useMemo(() => {
    const shown = branches.filter((b) => hubId === ALL || b.hubId === hubId);
    // Transit-aware: volume ships on `d` and ARRIVES at the branch on `d + lag`,
    // so for arrivals on `d` we read the origin's own volume from `d − lag`.
    const origVal = (oId: string, bId: string, dates: string[], lag: number) =>
      mode === "forecast"
        ? dates.reduce((s, d) => s + Math.round(hubVolOnDay(oId, lag > 0 ? addDays(d, -lag) : d) * shareOf(shares, oId, bId)), 0)
        : dates.reduce((s, d) => s + (actualIdx.get(`${oId}|${bId}|${d}`) ?? 0), 0);

    return shown
      .map((b) => {
        const home = hubById.get(b.hubId);
        const origins: OriginRow[] = hubs
          .map((o) => {
            const share = shareOf(shares, o.id, b.id);
            const lane = laneByPair.get(`${o.id}|${b.id}`);
            const routing: OriginRow["routing"] = o.id === b.hubId ? "Local" : lane?.routing === "direct" ? "Direct" : "H2H";
            const transit =
              routing === "Local"
                ? transitFor(b.hubId, b.lat, b.lng)
                : routing === "Direct"
                  ? transitFor(o.id, b.lat, b.lng)
                  : transitFor(o.id, home?.lat, home?.lng);
            // Apply the transit as a date lag only in transit-aware forecast mode.
            const lag = transitAware && mode === "forecast" ? transit ?? 0 : 0;
            const byPeriod = cols.map((p) => origVal(o.id, b.id, p.dates, lag));
            const total = byPeriod.reduce((s, v) => s + v, 0);
            return { hubId: o.id, hubName: o.name, routing, sharePct: share * 100, transit, byPeriod, total };
          })
          .filter((o) => o.total > 0 || o.sharePct > 0)
          .sort((a, b2) => (a.routing === "Local" ? -1 : b2.routing === "Local" ? 1 : b2.total - a.total));

        const byPeriod = cols.map((_, i) => origins.reduce((s, o) => s + o.byPeriod[i], 0));
        const total = byPeriod.reduce((s, v) => s + v, 0);
        return { id: b.id, name: b.name, hub: home?.name ?? "?", origins, byPeriod, total };
      })
      .sort((a, b) => b.total - a.total);
  }, [branches, hubId, hubs, hubById, shares, cols, mode, hubVolOnDay, actualIdx, laneByPair, transitAware]);

  const colTotals = useMemo(() => cols.map((_, i) => rows.reduce((s, r) => s + r.byPeriod[i], 0)), [rows, cols]);
  const grandTotal = useMemo(() => rows.reduce((s, r) => s + r.total, 0), [rows]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  const routeClass: Record<OriginRow["routing"], string> = {
    Local: "bg-slate-100 text-slate-600",
    H2H: "bg-sky-50 text-sky-700",
    Direct: "bg-emerald-50 text-emerald-700",
  };
  const toggle = (id: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-slate-900">Branch Forecast</h1>
        <span className="text-xs text-slate-400">{hubId === ALL ? "All hubs" : hubById.get(hubId)?.name}</span>
      </div>
      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-400">
        <span className="mt-px">ⓘ</span>
        <span>
          Every hub feeds every branch: a branch's volume = its home hub (<b>Local</b>) + <b>H2H</b> bits from all other
          hubs, each hub's slice from its share of that branch (seeded actuals). <b>Expand a branch</b> to see every origin
          hub (Local / H2H / Direct) with its share, transit and volume. Set a lane on the Routing Map to make an origin
          <b> Direct</b>.
        </span>
      </p>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {/* Controls */}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-52">
            <Field label="Hub">
              <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                <option value={ALL}>All hubs</option>
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-36">
            <Field label="Show">
              <Select value={mode} onChange={(e) => setMode(e.target.value as "forecast" | "actual")}>
                <option value="forecast">Forecast</option>
                <option value="actual">Actuals</option>
              </Select>
            </Field>
          </div>
          <div className="w-40">
            <Field label="From week of">
              <Input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value || today())} />
            </Field>
          </div>
          <div className="w-36">
            <Field label="Weeks">
              <Select value={String(numWeeks)} onChange={(e) => setNumWeeks(Number(e.target.value))}>
                {WEEK_OPTS.map((w) => (
                  <option key={w} value={w}>
                    {w} weeks{w === 52 ? " (year)" : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <label className="flex cursor-pointer items-center gap-2 pb-2 text-xs text-slate-600" title="Land H2H/Direct inbound on ship date + transit days instead of same-day">
              <input type="checkbox" checked={transitAware} onChange={(e) => setTransitAware(e.target.checked)} disabled={mode !== "forecast"} />
              Transit-aware arrivals
            </label>
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Branches" value={fmtN(rows.length)} sub={hubId === ALL ? "all hubs" : "in hub"} />
        <Kpi label={mode === "forecast" ? "Forecast" : "Actuals"} value={fmtN(grandTotal)} sub={`${numWeeks} weeks · all hubs`} />
        <Kpi label="Avg / week" value={fmtN(weeks.length ? Math.round(grandTotal / weeks.length) : 0)} sub="across branches" />
        <Kpi label="Feeding hubs" value={fmtN(hubs.length)} sub="Local + H2H per branch" />
      </div>

      {/* Branch × period */}
      <Card className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-2.5">
          <span className="text-sm font-medium text-slate-700">{mode === "forecast" ? "Forecast" : "Actuals"} · branch × week</span>
          <span className="text-[11px] text-slate-400">
            Click a week header to expand it to days{openWeeks.size > 0 && <> · <button className="text-brand-600 hover:underline" onClick={() => setOpenWeeks(new Set())}>collapse all</button></>}
          </span>
        </div>
        {rows.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No active branches{hubId === ALL ? "" : " in this hub"}.</p>
        ) : (
          <div className="max-h-[75vh] overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
                  <th className="sticky left-0 top-0 z-20 bg-white px-3 py-2 text-left font-medium">Branch</th>
                  <th className="px-3 py-2 text-left font-medium">Origin hub</th>
                  <th className="px-3 py-2 text-left font-medium">Routing</th>
                  <th className="px-3 py-2 text-right font-medium">Share</th>
                  <th className="px-3 py-2 text-right font-medium">Transit</th>
                  {weeks.map((w) =>
                    openWeeks.has(w.weekStart) ? (
                      w.dates.map((d, di) => (
                        <th
                          key={d}
                          onClick={di === 0 ? () => toggleWeek(w.weekStart) : undefined}
                          className={`px-2 py-2 text-right font-medium tabular-nums ${di === 0 ? "cursor-pointer hover:text-brand-700" : ""} ${di === 0 ? "border-l border-slate-200" : ""}`}
                          title={di === 0 ? "Click to collapse this week" : undefined}
                        >
                          {di === 0 ? "▾ " : ""}
                          {di === 0 && lockedWeeks.has(w.weekStart) ? "🔒 " : ""}
                          {fmtDow(d)}
                        </th>
                      ))
                    ) : (
                      <th
                        key={w.weekStart}
                        onClick={() => toggleWeek(w.weekStart)}
                        className="cursor-pointer px-2 py-2 text-right font-medium tabular-nums hover:text-brand-700"
                        title="Click to expand this week to days"
                      >
                        ▸ {lockedWeeks.has(w.weekStart) ? "🔒 " : ""}
                        {fmtDate(w.weekEnd)}
                      </th>
                    )
                  )}
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const max = Math.max(1, ...r.byPeriod);
                  const open = expanded.has(r.id);
                  return (
                    <Fragment key={r.id}>
                      <tr className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => toggle(r.id)}>
                        <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-medium text-slate-700">
                          <span className="mr-1 inline-block w-3 text-slate-400">{open ? "▾" : "▸"}</span>
                          {r.name}
                        </td>
                        <td className="px-3 py-1.5 text-slate-500">{r.hub}</td>
                        <td className="px-3 py-1.5" />
                        <td className="px-3 py-1.5" />
                        <td className="px-3 py-1.5" />
                        {r.byPeriod.map((v, i) => {
                          const alpha = v < 1 ? 0 : Math.min(0.8, 0.08 + (v / max) * 0.72);
                          return (
                            <td key={i} className="px-2 py-1.5 text-right tabular-nums" style={alpha ? ({ backgroundColor: `rgba(15,118,110,${alpha})`, color: alpha > 0.5 ? "white" : "#334155" } as CSSProperties) : undefined}>
                              {v < 1 ? "" : fmtN(v)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-1.5 text-right font-medium tabular-nums text-slate-700">{fmtN(r.total)}</td>
                      </tr>
                      {open &&
                        r.origins.map((o) => (
                          <tr key={o.hubId} className="border-b border-slate-100 bg-slate-50/60 text-[11px]">
                            <td className="sticky left-0 z-10 bg-slate-50 px-3 py-1" />
                            <td className="px-3 py-1 pl-5 font-medium text-slate-700">{o.hubName}</td>
                            <td className="px-3 py-1">
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${routeClass[o.routing]}`}>{o.routing}</span>
                            </td>
                            <td className="px-3 py-1 text-right font-medium tabular-nums text-emerald-600" title="share of this origin hub (from actuals)">
                              {o.sharePct.toFixed(1)}%
                            </td>
                            <td className="px-3 py-1 text-right tabular-nums text-slate-500">{o.transit == null ? "—" : `${o.transit}d`}</td>
                            {o.byPeriod.map((v, i) => (
                              <td key={i} className="px-2 py-1 text-right tabular-nums text-slate-500">
                                {v < 1 ? "" : fmtN(v)}
                              </td>
                            ))}
                            <td className="px-3 py-1 text-right tabular-nums text-slate-500">{fmtN(o.total)}</td>
                          </tr>
                        ))}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-medium text-slate-700">
                  <td className="sticky left-0 z-10 bg-white px-3 py-2">Total</td>
                  <td className="bg-white px-3 py-2" />
                  <td className="bg-white px-3 py-2" />
                  <td className="bg-white px-3 py-2" />
                  <td className="bg-white px-3 py-2" />
                  {colTotals.map((v, i) => (
                    <td key={i} className="px-2 py-2 text-right tabular-nums">
                      {fmtN(v)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums">{fmtN(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-lg font-semibold text-slate-900">{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </Card>
  );
}
